'use client';

import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import styles from './Invite.module.scss';
import Button, { ButtonType } from '../../../components/Button/Button';
import Input, { InputVariant } from '../../../components/Input/Input';
import Text from '@/components/Text/Text';
import { isValidEmail, getEmailErrorMessage } from '../../../helpers';
import apiClient from '@/apiClient';

interface InviteProps {
  data: {
    title: string;
    description: string;
    formDisclaimer: string;
    submitButton: {
      title: string;
      type: ButtonType;
    };
    emailPlaceholder: string;
  };
  isWhite?: boolean;
  className?: string;
  useGlobalClass?: boolean;
}

const Invite: React.FC<InviteProps> = ({
  data,
  isWhite,
  className,
  useGlobalClass = true,
}) => {
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<{
    variant: InputVariant;
    message: string;
  }>({
    variant: InputVariant.DEFAULT,
    message: '',
  });

  const t = useTranslations('invite');

  useEffect(() => {
    if (feedback.variant !== InputVariant.DEFAULT) {
      setEmail('');
      const timer = setTimeout(() => {
        setFeedback({ variant: InputVariant.DEFAULT, message: '' });
      }, 3_000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (feedback.variant !== InputVariant.DEFAULT) {
      setFeedback({ variant: InputVariant.DEFAULT, message: '' });
    }
  };

  const sendEmail = async (emailParam: string) => apiClient.post<{ success: boolean; message?: string }>('email', {
    cache: 'no-cache',
    body: JSON.stringify({
      email: emailParam,
      type: 'Website Invitation form submitted',
    }),
  });

  const handleSendClick = async () => {
    if (!isValidEmail(email)) {
      setFeedback({
        variant: InputVariant.ERROR,
        message: getEmailErrorMessage(email),
      });
      return;
    }

    try {
      await sendEmail(email);
      setFeedback({
        variant: InputVariant.SUCCESS,
        message: t('Email successfully sent!'),
      });
    } catch (error) {
      console.warn(error);
      setFeedback({
        variant: InputVariant.ERROR,
        message: t('Something went wrong, Please try again'),
      });
    }
  };

  return (
    <section
      className={clsx(styles.invite, 'custom', className, {
        [styles.isWhite]: isWhite,
      })}
    >
      <div
        className={clsx(styles.container, {
          'global-section': useGlobalClass,
        })}
      >
        <div className={styles.content}>
          <div className={styles.textColumn}>
            <Text
              tag='h2'
              className={styles.heading}
            >
              {data.title}
            </Text>
            <p className={styles.description}>{data.description}</p>
          </div>

          <div className={styles.formColumn}>
            <div className={styles.inputRow}>
              <Input
                type='email'
                showIcon
                placeholder={data.emailPlaceholder}
                value={email}
                onChange={handleEmailChange}
                className={styles.emailInput}
                isWhite={isWhite}
                variant={feedback.variant}
                text={feedback.message}
              />
              <div>
                <Button
                  type={data.submitButton.type}
                  onClick={handleSendClick}
                  iconName='arrow_forward'
                  className={styles.sendButton}
                >
                  {data.submitButton.title}
                </Button>
              </div>
            </div>
            <Text
              tag='p'
              className={styles.terms}
            >
              {data.formDisclaimer}
            </Text>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Invite;
